import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  DialogOverlay,
  DialogContainer,
  DialogHeader,
  DialogTitle,
  DialogCloseButton,
  DialogBody,
} from 'styles/common.styles';
import { useEscapeClose } from 'hooks/useEscapeClose';
import { theme } from 'styles/theme';
import {
  resolveDialogSize,
  saveDialogSize,
  clampDialogSize,
  DIALOG_SIZE_MIN,
} from 'components/common/dialogSizePrefs';

/**
 * 공통 Draggable Dialog
 *
 * - 헤더 드래그로 위치 이동
 * - 우·하·우하단 핸들로 크기 조절
 * - sizeKey + prefsUserId 있으면 크기를 localStorage에 사용자(기관|계정)별로 기억
 *
 * @param {string} [prefsUserId] - buildPrefsUserId(tenant, user)
 * @param {string} [sizeKey] - 기억 키 (예: codeLookup). 없으면 기억 안 함
 */
const DraggableDialog = ({
  open,
  title,
  onClose,
  dialogWidth = '860px',
  dialogHeight = '640px',
  keepMounted = true,
  escapeEnabled = true,
  prefsUserId = '',
  sizeKey = '',
  resizable = true,
  children,
}) => {
  const dialogWrapRef = useRef(null);
  const [dialogPos, setDialogPos] = useState({ x: 0, y: 0 });
  const [dragState, setDragState] = useState({ dragging: false, offsetX: 0, offsetY: 0 });
  const [size, setSize] = useState(() =>
    resolveDialogSize({ prefsUserId, sizeKey, dialogWidth, dialogHeight })
  );
  const [resizeSession, setResizeSession] = useState(null);
  const sizeKeyRef = useRef(sizeKey);
  const prefsUserIdRef = useRef(prefsUserId);
  sizeKeyRef.current = sizeKey;
  prefsUserIdRef.current = prefsUserId;

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEscapeClose(open, handleClose, { enabled: escapeEnabled !== false });

  // 키·기본값 변경 또는 열릴 때: 저장 크기 로드 + 중앙 배치 (리사이즈 중에는 호출되지 않음)
  useEffect(() => {
    const next = resolveDialogSize({ prefsUserId, sizeKey, dialogWidth, dialogHeight });
    setSize(next);
    if (!open) return;
    requestAnimationFrame(() => {
      const x = Math.max(16, Math.round((window.innerWidth - next.width) / 2));
      const y = Math.max(16, Math.round((window.innerHeight - next.height) / 2));
      setDialogPos({ x, y });
    });
  }, [open, prefsUserId, sizeKey, dialogWidth, dialogHeight]);

  useEffect(() => {
    if (!dragState.dragging) return undefined;

    const handleMouseMove = (e) => {
      const w = size.width;
      const h = size.height;
      let nextX = e.clientX - dragState.offsetX;
      let nextY = e.clientY - dragState.offsetY;
      nextX = Math.max(0, Math.min(nextX, window.innerWidth - w));
      nextY = Math.max(0, Math.min(nextY, window.innerHeight - h));
      setDialogPos({ x: nextX, y: nextY });
    };

    const handleMouseUp = () => {
      setDragState((prev) => ({ ...prev, dragging: false }));
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState.dragging, dragState.offsetX, dragState.offsetY, size.width, size.height]);

  useEffect(() => {
    if (!resizeSession) return undefined;

    const onMove = (e) => {
      const { edge, startX, startY, startW, startH, startLeft, startTop } = resizeSession;
      let nextW = startW;
      let nextH = startH;
      let nextX = startLeft;
      let nextY = startTop;

      if (edge.includes('e')) nextW = startW + (e.clientX - startX);
      if (edge.includes('s')) nextH = startH + (e.clientY - startY);
      if (edge.includes('w')) {
        const dx = e.clientX - startX;
        nextW = startW - dx;
        nextX = startLeft + dx;
      }
      if (edge.includes('n')) {
        const dy = e.clientY - startY;
        nextH = startH - dy;
        nextY = startTop + dy;
      }

      const clamped = clampDialogSize(nextW, nextH);
      if (edge.includes('w')) nextX = startLeft + (startW - clamped.width);
      if (edge.includes('n')) nextY = startTop + (startH - clamped.height);
      nextX = Math.max(0, Math.min(nextX, window.innerWidth - DIALOG_SIZE_MIN.width));
      nextY = Math.max(0, Math.min(nextY, window.innerHeight - DIALOG_SIZE_MIN.height));

      setSize(clamped);
      setDialogPos({ x: nextX, y: nextY });
      resizeSession.lastW = clamped.width;
      resizeSession.lastH = clamped.height;
    };

    const onUp = () => {
      const sk = sizeKeyRef.current;
      if (sk && resizeSession.lastW && resizeSession.lastH) {
        saveDialogSize(prefsUserIdRef.current, sk, resizeSession.lastW, resizeSession.lastH);
      }
      setResizeSession(null);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizeSession]);

  const startResize = useCallback(
    (edge, e) => {
      if (!resizable) return;
      e.preventDefault();
      e.stopPropagation();
      setResizeSession({
        edge,
        startX: e.clientX,
        startY: e.clientY,
        startW: size.width,
        startH: size.height,
        startLeft: dialogPos.x,
        startTop: dialogPos.y,
        lastW: size.width,
        lastH: size.height,
      });
    },
    [resizable, size.width, size.height, dialogPos.x, dialogPos.y]
  );

  if (!open && !keepMounted) return null;

  const handleStyle = {
    position: 'absolute',
    zIndex: 2,
    background: 'transparent',
  };

  return (
    <DialogOverlay style={{ display: open ? 'flex' : 'none' }}>
      <div
        ref={dialogWrapRef}
        style={{
          position: 'fixed',
          left: dialogPos.x,
          top: dialogPos.y,
        }}
      >
        <DialogContainer
          $width={`${size.width}px`}
          $height={`${size.height}px`}
          style={{ position: 'relative' }}
        >
          <DialogHeader
            onMouseDown={(e) => {
              if (resizeSession) return;
              const rect = dialogWrapRef.current?.getBoundingClientRect();
              if (!rect) return;
              e.preventDefault();
              setDragState({
                dragging: true,
                offsetX: e.clientX - rect.left,
                offsetY: e.clientY - rect.top,
              });
            }}
            style={{ cursor: 'move', userSelect: 'none' }}
          >
            <DialogTitle>{title}</DialogTitle>
            <DialogCloseButton
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleClose}
              aria-label="닫기"
              title="닫기 (Esc)"
            >
              ✕
            </DialogCloseButton>
          </DialogHeader>
          <DialogBody>{children}</DialogBody>

          {resizable ? (
            <>
              <div
                role="presentation"
                title="너비 조절"
                onMouseDown={(e) => startResize('e', e)}
                style={{
                  ...handleStyle,
                  top: 8,
                  right: 0,
                  width: 6,
                  bottom: 8,
                  cursor: 'ew-resize',
                }}
              />
              <div
                role="presentation"
                title="높이 조절"
                onMouseDown={(e) => startResize('s', e)}
                style={{
                  ...handleStyle,
                  left: 8,
                  right: 8,
                  bottom: 0,
                  height: 6,
                  cursor: 'ns-resize',
                }}
              />
              <div
                role="presentation"
                title="크기 조절 (사용자별 기억)"
                onMouseDown={(e) => startResize('se', e)}
                style={{
                  ...handleStyle,
                  right: 0,
                  bottom: 0,
                  width: 14,
                  height: 14,
                  cursor: 'nwse-resize',
                  background: `linear-gradient(135deg, transparent 50%, ${theme.colors.border} 50%)`,
                  borderBottomRightRadius: 10,
                }}
              />
            </>
          ) : null}
        </DialogContainer>
      </div>
    </DialogOverlay>
  );
};

export default DraggableDialog;
